import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersAPI } from '../services/api';

function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserDetail();
    fetchActivityLogs();
  }, [id]);

  const fetchUserDetail = async () => {
    try {
      const response = await usersAPI.getUserDetail(id);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      alert('Error loading user details');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const response = await usersAPI.getUserActivityLogs(id, { limit: 20 });
      setActivityLogs(response.data.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  if (loading) {
    return <div className="loading">Loading user details...</div>;
  }

  if (!user) {
    return <div className="error">User not found</div>;
  }

  return (
    <div>
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/users')}>
          ← Back to Users
        </button>
        <h1>User Details - {user.name}</h1>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Devices</h3>
          <div className="value">{user.statistics.total_devices}</div>
        </div>
        <div className="stat-card">
          <h3>Active Devices</h3>
          <div className="value">{user.statistics.active_devices}</div>
        </div>
        <div className="stat-card">
          <h3>Analysis Sessions</h3>
          <div className="value">{user.statistics.total_sessions}</div>
        </div>
        <div className="stat-card">
          <h3>Points Balance</h3>
          <div className="value">{user.statistics.points_balance}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Basic Information</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <p><strong>Email:</strong> {user.email}</p>
            <p><strong>Name:</strong> {user.name}</p>
            <p><strong>Gender:</strong> {user.gender || '-'}</p>
            <p><strong>Birth Year:</strong> {user.birth_year || '-'}</p>
            <p><strong>Serial Number:</strong> {user.serial_number || '-'}</p>
          </div>
          <div>
            <p><strong>Status:</strong> <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
              {user.is_active ? 'Active' : 'Inactive'}
            </span></p>
            <p><strong>Email Verified:</strong> {user.email_verified ? 'Yes' : 'No'}</p>
            <p><strong>Login Count:</strong> {user.login_count}</p>
            <p><strong>Last Login:</strong> {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : '-'}</p>
            <p><strong>Joined:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Profile Information</h2>
        </div>
        <div>
          <p><strong>Nickname:</strong> {user.profile.nickname || '-'}</p>
          <p><strong>Bio:</strong> {user.profile.bio || '-'}</p>
          <p><strong>Skin Type:</strong> {user.profile.skin_type || '-'}</p>
          <p><strong>Skin Tone:</strong> {user.profile.skin_tone || '-'}</p>
          <p><strong>Profile Completion:</strong> {user.profile.completion_percentage}%</p>
          <p><strong>Profile Public:</strong> {user.profile.is_public ? 'Yes' : 'No'}</p>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Recent Activity Logs</h2>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Activity Type</th>
                <th>IP Address</th>
                <th>Duration</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {activityLogs.map((log) => (
                <tr key={log.id}>
                  <td>{log.activity_type}</td>
                  <td>{log.ip_address || '-'}</td>
                  <td>{log.duration_seconds ? `${log.duration_seconds}s` : '-'}</td>
                  <td>{new Date(log.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default UserDetail;